/**
 * The one way a notification is opened (#1089, #1093) — from a panel row, an
 * inline action or a toast: open the resolved target, then mark it read
 * together with the other unread rows of its bundle (same `group_key`),
 * since they all lead to the same place.
 *
 * An app that is already open is reused (#1069): its frontmost window is
 * focused and gets the target's params (e.g. `tab`, `reportId`), which the
 * app picks up while mounted. A second Cortex window on top of the first one
 * only hid where the reply was. A target that names one particular object
 * (`documentId`, `recordingId`) keeps going through `openWindow`, whose own
 * identity check finds that object's window — repointing another document's
 * window would swap what the person was working on.
 */
import { useCallback } from 'react'
import type { Notification } from '@/services/notification-service'
import { notificationGroupKey, resolveNotificationTarget } from '@/lib/utils/notificationPayload'
import { reusableWindow } from '@/lib/window-identity'
import { useWindowsOptional } from '@/hooks/useWindows'
import { logError } from '@/lib/logger'
import { standaloneTargetPath, useStandaloneNotificationHost } from './notificationHost'

export function useOpenNotification(
  notifications: Notification[],
  markAsRead: (id: string) => Promise<void>,
): (notification: Notification) => boolean {
  const desktop = useWindowsOptional()
  const standalone = useStandaloneNotificationHost()

  const markBundleRead = useCallback(
    (notification: Notification) => {
      const groupKey = notificationGroupKey(notification)
      const toMark = groupKey
        ? notifications.filter((n) => !n.is_read && notificationGroupKey(n) === groupKey)
        : notification.is_read
          ? []
          : [notification]
      if (!notification.is_read && !toMark.some((n) => n.id === notification.id)) toMark.push(notification)
      for (const n of toMark) {
        markAsRead(n.id).catch((error) =>
          logError(error as Error, { context: 'NotificationCenter.open', notificationId: n.id }),
        )
      }
    },
    [notifications, markAsRead],
  )

  return useCallback(
    (notification: Notification) => {
      const target = resolveNotificationTarget(notification)
      if (!target) return false

      if (standalone) {
        const path = standaloneTargetPath(target, standalone.hasDesktop)
        if (path) standalone.navigate(path)
        markBundleRead(notification)
        return path !== null
      }

      if (!desktop) {
        logError(new Error('No host to open a notification target'), {
          context: 'NotificationCenter.open',
          notificationId: notification.id,
          appId: target.appId,
        })
        return false
      }
      const existing = reusableWindow(desktop.windows, target.appId, target.params)
      if (existing) {
        if (target.params) desktop.updateWindowParams(existing.id, target.params)
        desktop.focusWindow(existing.id)
      } else {
        void desktop.openWindow(target.appId, undefined, undefined, target.params)
      }
      markBundleRead(notification)
      return true
    },
    [standalone, desktop, markBundleRead],
  )
}
